import {
  CalendarDaysIcon,
  ClockIcon,
  DocumentTextIcon,
  HashtagIcon,
  UserGroupIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCurrentCompany } from "api/company.api";
import { FeatureFlagName } from "api/featureFlag.api";
import { deleteUppdrag, patchUppdrag, UppdragQueryKey } from "api/uppdrag.api";
import { DotMenu } from "components/DotMenu";
import {
  FilePreviewSmall,
  InspectionPDFPreviewSmall,
} from "components/FilePreviewSmall";
import { WasteStationNotificationStatusBadge } from "components/WasteStationNotificationStatusBadge";
import { formatDate } from "helpers/date";
import { isPdf } from "helpers/file";
import { useIsFeatureFlagEnabled } from "hooks/useFeatureFlag";
import { useNotification } from "hooks/useNotification";
import { getPdfProxyUrl, usePreviewPdf } from "hooks/usePreviewPdf";
import { PriorityIcon } from "pages/app/uppdrag-list-page/PriorityIcon";
import { useState } from "react";
import {
  getUppdragDateAsString,
  Uppdrag,
  UppdragStatus,
  useIsCurrentUserAuthorizedForModifyingUppdrag,
} from "types/uppdrag.type";
import { DetailTile, FacilityTile } from "../operator/DetailTiles";
import { OperatorUppgiftList } from "../operator/OperatorUppgiftList";
import { OperatorDangerSheet } from "../transportSedel/operator/OperatorDangerSheet";
import { PatchUppdragModal } from "./UppdragModal";
import { UppdragStatusButton } from "./UppdragStatusButton";
import { usePatchUppdragLogic } from "./useCreateOrUpdateUppdragLogic";

type Props = {
  uppdrag: Uppdrag;
};

export const UppdragOverviewOperator = ({ uppdrag }: Props) => {
  const logic = usePatchUppdragLogic({ uppdragToEdit: uppdrag });

  const { data: currentCompany } = useCurrentCompany();
  const canModify = useIsCurrentUserAuthorizedForModifyingUppdrag(uppdrag);

  const { previewPdf, isLoading, currentUrl } = usePreviewPdf();

  const isWasteStationNotificationEnabled = useIsFeatureFlagEnabled(
    FeatureFlagName.IsWasteStationNotificationEnabled,
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const n = useNotification();

  const deleteMutation = useMutation({
    ...deleteUppdrag(uppdrag.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
      navigate({ to: "/app/uppdrag" });
    },
    onError: () => {
      n.error();
      setIsDeleteModalOpen(false);
    },
  });

  const completeMutation = useMutation({
    ...patchUppdrag(),
    onSuccess: () => {
      n.success("Uppdraget markerat som slutfört");
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
    },
    onError: () => n.error(),
  });

  const canBeCompleted =
    uppdrag.status !== UppdragStatus.Completed &&
    !!uppdrag.uppgiftCount &&
    uppdrag.areAllUppgiftsCompleted;

  const scheduledDate = getUppdragDateAsString(uppdrag);
  const timeReportData = uppdrag.timeReportData;
  const formatHours = (reported?: string | null, estimated?: string | null) =>
    reported
      ? `${reported} h`
      : estimated
        ? `${estimated} h (uppskattat)`
        : "";
  const workedTime = formatHours(
    timeReportData?.reportedWorkingHours,
    timeReportData?.estimatedWorkingHours,
  );
  const drivingTime = formatHours(
    timeReportData?.reportedDrivingHours,
    timeReportData?.estimatedDrivingHours,
  );

  return (
    <div className="max-w-3xl overflow-hidden rounded-2xl border border-gray-100 bg-white shadow">
      <div className="flex items-center justify-between gap-x-4 border-b border-gray-75 bg-gray-12 px-4 py-3 sm:px-6">
        <UppdragStatusButton uppdrag={uppdrag} />
        {canModify && (
          <DotMenu
            items={[
              {
                label: "Redigera",
                onClick: logic.openModal,
              },
              {
                label: "Ta bort uppdrag",
                onClick: () => setIsDeleteModalOpen(true),
                isDelete: true,
              },
            ]}
          />
        )}
      </div>

      <div className="flex flex-col gap-y-6 px-4 py-5 sm:px-6">
        {canBeCompleted && (
          <div className="flex flex-col gap-3 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 xs:flex-row xs:items-center">
            <CheckCircleIcon
              className="hidden h-7 w-7 shrink-0 text-cyan-500 xs:block"
              aria-hidden="true"
            />
            <div className="flex-1">
              <p className="font-semibold text-cyan-900">
                Alla uppgifter avklarade
              </p>
              <p className="text-sm text-cyan-800">
                Vill du markera uppdraget som slutfört?
              </p>
            </div>
            <button
              type="button"
              disabled={completeMutation.isPending}
              onClick={() =>
                completeMutation.mutate({
                  id: uppdrag.id,
                  payload: { status: UppdragStatus.Completed },
                })
              }
              className="h-11 shrink-0 rounded-lg bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors active:bg-cyan-700 disabled:bg-gray-400"
            >
              {completeMutation.isPending
                ? "Sparar..."
                : "Markera som slutfört"}
            </button>
          </div>
        )}
        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Detaljer
          </h4>
          <div className="grid grid-cols-1 gap-2 xs:grid-cols-2">
            <FacilityTile facility={uppdrag.facility} label="Anläggning" />
            <DetailTile
              icon={CalendarDaysIcon}
              label="Schemalagt datum"
              value={
                scheduledDate ? (
                  <span className="flex items-center gap-x-1.5">
                    {scheduledDate}
                    {uppdrag.isPrioritized && <PriorityIcon />}
                  </span>
                ) : undefined
              }
            />
            <DetailTile
              icon={UserIcon}
              label="Operatör"
              value={uppdrag.assignedToUser?.name}
            />
            <DetailTile
              icon={HashtagIcon}
              label="Referens"
              value={uppdrag.referenceNumber}
            />
            {currentCompany?.isInvoicingEnabled && (
              <DetailTile
                icon={ClockIcon}
                label="Tidsrapportering"
                value={
                  workedTime || drivingTime ? (
                    <span className="flex items-center gap-x-1">
                      <span className="flex flex-col">
                        <span>{`Arbetad tid: ${workedTime}`}</span>
                        <span>{`Körtid: ${drivingTime}`}</span>
                      </span>
                      {timeReportData?.isTimeReportConfirmed && (
                        <CheckCircleIcon className="h-5 w-5 text-cyan-500" />
                      )}
                    </span>
                  ) : undefined
                }
              />
            )}
            {!!uppdrag.parentCustomer && (
              <DetailTile
                icon={UserGroupIcon}
                label="Huvudkund"
                value={uppdrag.parentCustomer.name}
              />
            )}
            {!!uppdrag.description && (
              <DetailTile
                icon={DocumentTextIcon}
                label="Uppdragsbeskrivning"
                value={uppdrag.description}
                className="xs:col-span-2"
              />
            )}
          </div>
        </section>

        {isWasteStationNotificationEnabled &&
          !!uppdrag.wasteStationNotification && (
            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Förfrågan till mottagarstation
              </h4>
              <div className="flex flex-col gap-y-3 rounded-xl border border-gray-75 bg-white p-4">
                <div className="flex flex-wrap items-center gap-x-1 gap-y-1 text-sm text-gray-900">
                  <span>
                    {`Lämningsdatum ${formatDate(
                      uppdrag.wasteStationNotification.requestedDate,
                    )}`}
                  </span>
                  <WasteStationNotificationStatusBadge
                    notification={uppdrag.wasteStationNotification}
                  />
                </div>
                {!!uppdrag.wasteStationNotification.messageFromWasteStation && (
                  <div className="max-w-sm self-start whitespace-pre-wrap rounded-2xl rounded-tl-sm border border-gray-75 bg-gray-25 px-3.5 py-2.5 text-gray-800">
                    {uppdrag.wasteStationNotification.messageFromWasteStation}
                  </div>
                )}
                {uppdrag.wasteStationNotification.fileList.length > 0 && (
                  <div className="flex flex-col gap-y-2">
                    {uppdrag.wasteStationNotification.fileList.map((f) =>
                      isPdf(f.name) ? (
                        <InspectionPDFPreviewSmall
                          key={f.id}
                          inspectionName={f.nameWithoutExtension as string}
                          isLoading={
                            isLoading && currentUrl === getPdfProxyUrl(f.url)
                          }
                          onClick={() => previewPdf(getPdfProxyUrl(f.url))}
                        />
                      ) : (
                        <FilePreviewSmall
                          key={f.id}
                          url={f.url}
                          name={`${f.name}`}
                        />
                      ),
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

        <OperatorUppgiftList uppdrag={uppdrag} />
      </div>

      <PatchUppdragModal logic={logic} />
      <OperatorDangerSheet
        isOpen={isDeleteModalOpen}
        title="Ta bort uppdrag"
        actionText="Ta bort uppdrag"
        actionHandler={() => deleteMutation.mutate()}
        isLoading={deleteMutation.isPending}
        onClose={() => setIsDeleteModalOpen(false)}
        paragraphs={["Är du säker på att du vill ta bort uppdraget?"]}
      />
    </div>
  );
};
